import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, inr, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { ArrowsClockwise, Bank, Coins } from "@phosphor-icons/react";

export default function CashBankPosition() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/cash-bank-position");
            setData(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const banks = (data?.accounts || []).filter((a) => a.type === "bank");
    const cash = (data?.accounts || []).filter((a) => a.type === "cash");

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Cash & Bank Position"
                sub={`Live closing balance across every bank and cash account. As of ${data?.as_of ? formatDate(data.as_of) : "…"}.`}
                right={
                    <Button variant="outline" onClick={refresh} disabled={loading} data-testid="cbp-refresh">
                        <ArrowsClockwise size={14} className="mr-2" /> Refresh
                    </Button>
                }
            />

            <div className="p-8 space-y-6">
                <div className="grid gap-4 md:grid-cols-3">
                    <SummaryCard label="Banks total" value={data?.total_bank} icon={<Bank size={20} weight="duotone" style={{ color: "#0d6efd" }} />} testid="cbp-total-bank" />
                    <SummaryCard label="Cash in hand" value={data?.total_cash} icon={<Coins size={20} weight="duotone" style={{ color: "#f59e0b" }} />} testid="cbp-total-cash" />
                    <SummaryCard label="Grand total" value={data?.grand_total} bold accent testid="cbp-grand-total" />
                </div>

                <SectionCard title="Bank accounts" accent="#0d6efd" testid="cbp-banks">
                    <AccountList accounts={banks} loading={loading} />
                </SectionCard>

                <SectionCard title="Cash accounts" accent="#f59e0b" testid="cbp-cash">
                    <AccountList accounts={cash} loading={loading} />
                </SectionCard>

                <div className="text-[11px] text-stone-500">
                    Balances are computed live from the ledger. Click any account to open its full transaction history.
                </div>
            </div>
        </>
    );
}

function AccountList({ accounts, loading }) {
    if (loading && accounts.length === 0) {
        return <div className="p-6 text-center text-stone-500 text-sm">Loading…</div>;
    }
    if (accounts.length === 0) {
        return <div className="p-6 text-center text-stone-500 text-sm">No accounts in this category.</div>;
    }
    return (
        <table className="se-table">
            <thead><tr><th>Account</th><th className="text-right">Balance</th><th></th></tr></thead>
            <tbody>
                {accounts.map((a) => (
                    <tr key={a.id} data-testid={`cbp-row-${a.id}`}>
                        <td className="font-medium">{a.name}</td>
                        <td className="se-num text-right font-medium" style={{ color: a.balance < 0 ? "#dc2626" : "#0f172a" }}>
                            ₹ {inr(a.balance)}
                        </td>
                        <td className="text-right">
                            <Link to={`/ledgers/${a.id}`} className="text-[11px] underline text-blue-700" data-testid={`cbp-open-${a.id}`}>
                                open →
                            </Link>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function SummaryCard({ label, value, icon, bold, accent, testid }) {
    return (
        <div className="border rounded-md px-5 py-4"
             style={{ borderColor: "var(--se-border)", background: accent ? "linear-gradient(135deg, #f0fdfa, #fff)" : "#fff" }}
             data-testid={testid}>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-stone-500">
                {icon} {label}
            </div>
            <div className={`se-num text-3xl mt-2 ${bold ? "font-medium" : ""}`} style={{ color: accent ? "#0f766e" : "#0f172a" }}>
                ₹ {inr(value || 0)}
            </div>
        </div>
    );
}

function SectionCard({ title, children, accent, testid }) {
    return (
        <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }} data-testid={testid}>
            <div className="px-5 py-3 border-b" style={{ borderColor: "var(--se-border)" }}>
                <div className="font-serif text-lg" style={accent ? { color: accent } : undefined}>{title}</div>
            </div>
            {children}
        </div>
    );
}
